import { createSlice } from "@reduxjs/toolkit";
// api client
import apiClient from "./apiClient.js";
import { toGraphQLQuery } from "./graphQLHelpers.js";

const initialState = {
  coordSysX: [],
  coordSysY: [],
  measure: [],
  selectedCoordSysX: 0,
  selectedCoordSysY: 0,
  selectedMeasure: 0,
  all: [],
};

const REQUESTS = {
  index: () => {
    return `query {
      inspections{
        id,
        name,
        potreeLayers{
          name,
          id
        }
      }
    }
    `;
  },
  get: (id) => {
    return `query {
      inspection(id: "${id}"){
        id,
        name,
       coordSysX {
          name,
          unit,
          refs {
            coord,
            value
          },
          hidden
        },
        coordSysY {
          name,
          unit,
          refs {
            coord,
            value
          },
          hidden
        },
        measure {
          name,
          coordSys {
            axis,
            name
          }
        },
        referenceUnit {
          name,
          unit
        }
      }
    }`;
  },
  delete: (id) => {
    return `mutation {
      deleteInspection(id:"${id}")
    }`;
  },
  update: ({ id, name, missionId }) => {
    const query = {
      id,
      ...(name && { name }),
      ...(missionId && { missionId })
    };
  
    return `mutation {
      editInspection(inspection: ${toGraphQLQuery(query)}) {
        id
        name
        missionId
      }
    }`;
  },  
  create: ({ name, missionId }, outputFields = `id,name,missionId`) => {
    return `mutation {
      createInspection(inspection: {
        name: "${name}",
        missionId: "${missionId}",
        coordSysX: [
          {
            name: "m",
            unit: "m",
            refs: [{ coord: 0, value: 0 }, { coord: 1, value: 1 }],
            hidden: false
          }
        ],
        coordSysY: [
          {
            name: "m",
            unit: "m",
            refs: [{ coord: 0, value: 0 }, { coord: 1, value: 1 }],
            hidden: false
          }
        ],
        measure: [
          {
            name: "m",
            coordSys: [
              { axis: "x", name: "m" },
              { axis: "y", name: "m" }
            ]
          }
        ],
        referenceUnit: {
          unit: "m",
          name: "m"
        }
      }) {
        ${outputFields}
      }
    }`;
  }  
};

export const inspectionApi = {
  get: async (id, next) => {
    //
    const result = await apiClient.req(REQUESTS.get(id));
    //
    next(result.data.data.inspection);
  },
  index: async (next) => {
    const result = await apiClient.req(REQUESTS.index());
    next(result.data.data.inspections);
  },
  delete: async (id, next) => {
    const result = await apiClient.req(REQUESTS.delete(id));
    next(result.data.data.deleteInspection);
  },
  update: async (payload, next) => {
    // Make main request
    const result = await apiClient.req(REQUESTS.update({ ...payload}));
    next(result.data.data.editInspection);
  },
  create: async ({ name, missionId }, outputFields, next) => {
    const result = await apiClient.req(REQUESTS.create({ name, missionId }, outputFields));
    next(result.data.data.createInspection);
  },
};

export const selectUnits = (state) => {
  // units
  const units = [];
  state.inspection.coordSysX.forEach((el) => {
    if (!units.some((u) => u === el.unit)) {
      units.push(el.unit);
    }
  });
  state.inspection.coordSysY.forEach((el) => {
    if (!units.some((u) => u === el.unit)) {
      units.push(el.unit);
    }
  });
  return units;
};

export const selectPositionColumns = (state) => {
  // units
  const units = ["px"];
  state.inspection.coordSysX.forEach((el) => {
    if (!units.some((u) => u === el.unit)) {
      units.push(el.unit);
    }
  });
  state.inspection.coordSysY.forEach((el) => {
    if (!units.some((u) => u === el.unit)) {
      units.push(el.unit);
    }
  });
  // let's compute the position columns
  return [
    {
      axis: "x",
      name: "px",
      unit: "px",
    },
    {
      axis: "y",
      name: "px",
      unit: "px",
    },
    ...state.inspection.coordSysX.map((el) => {
      return {
        axis: "x",
        ...el,
      };
    }),
    ...state.inspection.coordSysY.map((el) => {
      return {
        axis: "y",
        ...el,
      };
    }),
  ];
};

export const inspectionSlice = createSlice({
  name: "inspection",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setInspection: (state, { payload: { inspection } }) => {
      return {
        ...state,
        ...inspection,
      };
    },
    setInspections: (state, { payload: { inspections } }) => {
      return {
        ...state,
        all: inspections,
      };
    },
  },
});

export const { setInspection, setInspections } = inspectionSlice.actions;

const pt2CoordSys = ({ pt, sys, axis }) => {
  let min = sys.refs[axis];
  let max = sys.refs[axis];
  sys.refs.forEach((el) => {
    if (el.coord < min.coord) {
      min = el;
    }
    if (el.coord > max.coord) {
      max = el;
    }
  });
  return {
    pos:
      min.value +
      ((pt[axis] - min.coord) / (max.coord - min.coord)) *
        (max.value - min.value),
    unit: sys.unit,
  };
};

export const selectAllInspections = (state) => state.inspection.all;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectMetricSystem = (state) => {
  return (pt) => {
    let output = {};
    // compute the X pos :
    const xSys = state.inspection.coordSysX[state.inspection.selectedCoordSysX];
    if (xSys !== undefined) {
      const r = pt2CoordSys({ pt, sys: xSys, axis: 0 });
      output.x = r.pos;
      output.xUnit = r.unit;
    }
    // compute the Y pos :
    const ySys = state.inspection.coordSysY[state.inspection.selectedCoordSysY];
    if (ySys !== undefined) {
      const r = pt2CoordSys({ pt, sys: ySys, axis: 1 });
      output.y = r.pos;
      output.yUnit = r.unit;
    }
    return output;
  };
};

// If we have a unique correspondance from reference-unit to meters and unique resolution in X and Y, returns the resolution in meters/unit
// Otherwise, returns null
export const selectResolutionMetersPerUnit = (state) => {
  const xSys = state.inspection.coordSysX[state.inspection.selectedCoordSysX];
  const ySys = state.inspection.coordSysY[state.inspection.selectedCoordSysY];
  if (xSys == null || ySys == null) return null;
  if (xSys.unit != "m") return null;
  if (ySys.unit != "m") return null;
  let res_x = (xSys.refs[1].value-xSys.refs[0].value)/(xSys.refs[1].coord-xSys.refs[0].coord);
  let res_y = (ySys.refs[1].value-ySys.refs[0].value)/(ySys.refs[1].coord-ySys.refs[0].coord);
  if (Math.abs(res_x-res_y)/(res_x+res_y) < 1e-6) return Math.abs(res_x); // same resolution in X and Y
  else return null;
};

export const selectReferenceUnit = (state) => state.inspection.referenceUnit;

/* take annotation position and a coordSys as params
    for each field in position we check if it possesses data for the coordSys selected
    and returns an object output containing the coordSys values of those field
    exemple:
      output= {
        // center is a special case sturcture this way:
        center: {
          x: {axis, unit, name, value} // coordSys value of position
          y: {axis, unit, name, value} // coordSys value of position
        },
        // all other fields structureed this way
        area: {value, unit, name} // coordSys value of position
      }
  */
export const pos2InspectionCoordSys = (selectedCordSys, position) => {
  // Early return if selectedCordSys is invalid
  if (!selectedCordSys || !selectedCordSys.x || !selectedCordSys.y || !position) {
    return {};
  }
  
  const output = {};
  Object.entries(position).map(([k,v]) => {
    if (!v) return; // Skip if v is undefined/null
    
    if (k === "center") {
      // Filter out undefined/null values and check for matching units
      const xFiltered = v.x?.filter(sys => sys && sys.unit === selectedCordSys.x.unit) || [];
      const yFiltered = v.y?.filter(sys => sys && sys.unit === selectedCordSys.y.unit) || [];
      if (xFiltered.length > 0 && yFiltered.length > 0) {
        output[k] = {
          x: xFiltered[0],
          y: yFiltered[0]
        }
      }
    } else {
      // Filter out undefined/null values and check for matching units
      const filtered = Array.isArray(v) ? v.filter(sys => sys && sys.unit === selectedCordSys.x.unit) : [];
      if (selectedCordSys.x.unit === selectedCordSys.y.unit && filtered.length > 0) {
          output[k] = filtered[0]
        }
    }
  })
  return output;
}

export const selectCoordSys= (state) => {
  return {
    x: state.inspection.coordSysX[state.inspection.selectedCoordSysX],
    y: state.inspection.coordSysY[state.inspection.selectedCoordSysY]
  }
};

export const selectScreenRuler = (state) => {
  return ({ pt1, pt2, currentResolutionInMap }) => {
    let unit = "";
    if (currentResolutionInMap == null) return undefined;
    // get the map pts
    const mapPt1 = pt1.map((el) => el * currentResolutionInMap);
    const mapPt2 = pt2.map((el) => el * currentResolutionInMap);
    // get the measure
    const mes = state.inspection.measure[state.inspection.selectedMeasure];
    if (mes === undefined) {
      return undefined;
    }
    // select all the coord sys
    const posInCoordSys = mes.coordSys.map((cs) => {
      if (cs.axis === "x") {
        const metricSys = state.inspection.coordSysX.filter(
          (csx) => csx.name === cs.name
        )[0];
        if (unit === "") {
          unit = metricSys.unit;
        }
        return {
          pt1: pt2CoordSys({
            pt: mapPt1,
            sys: metricSys,
            axis: 0,
          }),
          pt2: pt2CoordSys({
            pt: mapPt2,
            sys: metricSys,
            axis: 0,
          }),
        };
      } else {
        const metricSys = state.inspection.coordSysY.filter(
          (csx) => csx.name === cs.name
        )[0];
        return {
          pt1: pt2CoordSys({
            pt: mapPt1,
            sys: metricSys,
            axis: 1,
          }),
          pt2: pt2CoordSys({
            pt: mapPt2,
            sys: metricSys,
            axis: 1,
          }),
        };
      }
    });
    let resu = 0;
    posInCoordSys.forEach((el) => {
      resu += Math.pow(el.pt2.pos - el.pt1.pos, 2);
    });
    return {
      dist: Math.sqrt(resu),
      unit,
    };
  };
};

export default inspectionSlice.reducer;
